/* import __COLOCATED_TEMPLATE__ from './schematic.hbs'; */
import Component from '@glimmer/component';
import { dasherize } from '@ember/string';
import { formatFieldValue } from '../../utils/format';
import FilterConstants from '../../filter-constants';
import { isNearInfinity } from '../../utils/common';
import { getOpAmpReferenceDesignator } from '../../utils/materials';
import type ComponentsInput from '../../models/inputs/component-selection';
import type TolerancesInput from '../../models/inputs/tolerance';
import type DetailsOfCircuit from '../../models/results/sub-results/circuit-details';

interface SchematicFigureArgs {
  model: SchematicModel;
  showHelp?: (title: string, message: string, msgparams?: Record<string, unknown>) => void;
}

type SchematicModel = {
  detailsOfCircuits: DetailsOfCircuit[];
  detailsOfStages: DetailsOfStage[];
  billOfMaterials: { designators: string; value: string; tolerance: string }[];
  needReferenceStage: boolean;
  componentsInput: ComponentsInput;
  toleranceInput: TolerancesInput;
};

export type DetailsOfStage = {
  opAmp: string;
  opAmpSource: { getOpAmp: (opAmp: string) => OpAmp };
  stageLetter: string;
};

type OpAmp = {
  numberChannels: number;
  packageCode: string;
};

type CircuitConstants = {
  xTranslate: number;
  yTranslate: number;
  width: number;
  height: number;
  offset: number;
};

type ActualStageResult = {
  name: string;
  circuitId: string;
  stage: DetailsOfStage;
  node: { [key in 'in' | 'out' | 'ref']: NodeDetail };
  details: StageDetails;
  geometry: { [key in 'xTranslate' | 'yTranslate' | 'width' | 'height']: number };
  values: DetailsOfCircuit;
  designationR: string;
  designationC: string;
};

type NodeDetail = {
  showLine: boolean;
  showText: boolean;
};

type StageDetails = {
  stageFilterType: string;
  stageImplementation: string;
  stageLetter: string;
  stageOrder: string | number;
};

type NeedReferenceStage = {
  display: boolean;
  circuitName: string;
};

type NeedBypassCapacitor = {
  display: boolean;
  bypassCapacitors: BypassCapacitor[];
};

type NeedOpAmpSpare = {
  display: boolean;
  opAmpSpares: OpAmpSpare[];
};

type BypassCapacitor = {
  item: string;
  value: string;
  tolerance: string;
  voltage: string;
  circuit: string;
};

type OpAmpSpare = {
  channelDesignator: string;
  circuit: string;
  name: string;
  referenceDesignator: string;
  vsMinus: string;
  vsPlus: string;
};

type ReferenceValues = {
  [key in 'v1' | 'v2' | 'c1' | 'c2' | 'c3' | 'c4' | 'r1' | 'r2' | 'r3' | 'r4' | 'opAmp']: ReferenceValueItem;
};

type ReferenceValueItem = {
  name: string;
  value: string | number | null;
  url?: string | null;
  tolerance?: string | null;
};

type BypassRef = {
  ref: string;
  voltage: string;
  circuit: string;
};

type Bom = {
  designators: string;
  value: string;
  tolerance: string;
};

type TantalumRef = {
  ref: string;
  voltage: string;
  circuit: string;
};

export default class SchematicFigure extends Component<SchematicFigureArgs> {
  width = 700;
  height = 460;

  leftMargin = 0;
  topMargin = 0;
  rightMargin = 0;
  bottomMargin = 0;

  get outTxtPos(): number {
    const model = this.args.model;
    const circuitDetails = model.detailsOfCircuits;

    let value = 2.5;
    let x1 = 0;
    let x2 = 0;
    if (circuitDetails && circuitDetails.length > 0) {
      for (let i = 0; i < circuitDetails.length; i++) {
        if (circuitDetails[i]) {
          const dimensions = this.getCircuitConstants(
            this.getCircuitName(circuitDetails[i] as DetailsOfCircuit).toLowerCase(),
          );
          if (i === 0) {
            if (dimensions.offset < 0) {
              x1 = dimensions.offset;
            }
            x2 = x1 + dimensions.width;
            if (dimensions.offset < 0) {
              x2 += dimensions.offset * -1;
            }
          } else {
            x1 = x2;
            x2 = x1 + dimensions.width;
          }
        }
      }
      value = x2;
    }
    return value;
  }

  get actualStages(): ActualStageResult[] {
    let x1 = 0;
    let x2 = 0;
    const principalOffset = 0;
    const result = [];
    const subDirectory = 'circuits/';
    const model = this.args.model;
    const showReference = model.needReferenceStage;
    const circuitDetails = model.detailsOfCircuits;

    if (circuitDetails && circuitDetails.length > 0) {
      for (let i = 0; i < circuitDetails.length; i++) {
        if (circuitDetails[i]) {
          const circuitItem = circuitDetails[i] as DetailsOfCircuit,
            filterType = circuitItem.filter.name,
            implementation = circuitItem.implementation,
            stage = circuitItem.stage;

          let referenceDesignator = '',
            channelDesignator = '';

          if (this.displayExtraComponents) {
            const opAmpName = stage.opAmp;
            const opAmpData = stage.opAmpSource.getOpAmp(opAmpName);
            const opAmpChannels = opAmpData.numberChannels;
            const opAmpStageLetter = stage.stageLetter;

            referenceDesignator = getOpAmpReferenceDesignator(opAmpData.packageCode, opAmpChannels) + opAmpStageLetter;
            if (opAmpChannels > 1) {
              channelDesignator = ('1/' + opAmpChannels.toString()).toUpperCase();
            }
          }

          const nodeIn = i === 0 ? { showLine: false, showText: true } : { showLine: true, showText: false };
          const nodeOut =
            i === circuitDetails.length - 1 ? { showLine: false, showText: true } : { showLine: true, showText: false };
          const nodeRef = showReference ? { showLine: false, showText: true } : { showLine: true, showText: false };

          const circuitName = this.getCircuitName(circuitItem);
          const dimensions = this.getCircuitConstants(circuitName.toLowerCase());

          if (i === 0) {
            x1 = principalOffset;
            if (dimensions.offset < 0) x1 = dimensions.offset;
            x2 = x1 + dimensions.width;
            if (dimensions.offset < 0) x2 += dimensions.offset * -1;
          } else {
            x1 = x2;
            x2 = x1 + dimensions.width;
          }
          result.push({
            name: subDirectory + circuitName.toLowerCase(),
            circuitId: circuitName.toLowerCase() + '_' + i,
            stage: stage,
            node: {
              in: nodeIn,
              out: nodeOut,
              ref: nodeRef,
            },
            details: {
              stageLetter: circuitItem.stageLetter,
              stageOrder: circuitItem.order || '% order',
              stageFilterType: dasherize(filterType),
              stageImplementation: dasherize(this.getImplementation(implementation)),
            },
            geometry: {
              xTranslate: x1,
              yTranslate: dimensions.yTranslate,
              width: dimensions.width,
              height: dimensions.height,
            },
            values: circuitItem,
            designationR: referenceDesignator,
            designationC: channelDesignator,
          });
        }
      }
    }
    // if (result.length>0) {
    //     result = this.useTrialData(circuitDetails[0]);
    // }
    return result;
  }

  get needReferenceStage(): NeedReferenceStage {
    return {
      display: this.args.model.needReferenceStage,
      circuitName: 'circuits/' + FilterConstants.fwCircuits.referenceCircuit,
    };
  }

  get displayExtraComponents(): boolean {
    const bom = this.args.model.billOfMaterials;
    return typeof bom !== 'undefined' && bom !== null && bom.length > 0;
  }

  get bypassCapacitors(): BypassCapacitor[] {
    const capacitorList = [];
    if (this.displayExtraComponents) {
      const bom = this.args.model.billOfMaterials;
      const stages = this.args.model.detailsOfStages;
      const vsPlus = this.args.model.componentsInput.vsPlus;
      const vsMinus = this.args.model.componentsInput.vsMinus;
      // Bypass Capacitors for Stages
      for (let i = 0; i < stages.length; i++) {
        const bypassRef = [];
        bypassRef.push({
          ref: FilterConstants.bypassCapacitorNames.c9,
          voltage: formatFieldValue(vsPlus, FilterConstants.fieldTypes.NumericDecimal, '3s') + 'V',
          circuit: FilterConstants.fwCircuits.bypassCapacitor,
        });
        bypassRef.push({
          ref: FilterConstants.bypassCapacitorNames.c0,
          voltage: formatFieldValue(vsMinus, FilterConstants.fieldTypes.NumericDecimal, '3s') + 'V',
          circuit: FilterConstants.fwCircuits.bypassCapacitor,
        });
        for (let j = 0; j < bypassRef.length; j++) {
          if (bypassRef[j] && stages[i]) {
            const bypassRefItem = bypassRef[j] as BypassRef,
              stagesItem = stages[i] as DetailsOfStage;

            const item = bypassRefItem.ref + stagesItem.stageLetter;
            const vsupply = bypassRefItem.voltage;
            const circuitName = bypassRefItem.circuit;
            for (let k = 0; k < bom.length; k++) {
              if (bom[k]) {
                const bomItem = bom[k] as Bom;
                const designators = bomItem.designators,
                  value = bomItem.value,
                  tolerance = bomItem.tolerance;
                if (designators.includes(item)) {
                  capacitorList.push({
                    item: item,
                    value: value,
                    tolerance: tolerance,
                    voltage: vsupply,
                    circuit: circuitName,
                  });
                  break;
                }
              }
            }
          }
        }
      }
      // Tantalum and C9M Bypass Reference Capacitors
      const tantalumRef = [];
      tantalumRef.push({
        ref: FilterConstants.tantalumCapacitorNames.c101m,
        voltage: formatFieldValue(vsPlus, FilterConstants.fieldTypes.NumericDecimal, '3s') + 'V',
        circuit: FilterConstants.fwCircuits.tantalumCapcacitorPositive,
      });
      tantalumRef.push({
        ref: FilterConstants.tantalumCapacitorNames.c100m,
        voltage: formatFieldValue(vsMinus, FilterConstants.fieldTypes.NumericDecimal, '3s') + 'V',
        circuit: FilterConstants.fwCircuits.tantalumCapcacitorNegative,
      });
      tantalumRef.push({
        ref: FilterConstants.bypassCapacitorNames.c9 + 'M',
        voltage: formatFieldValue(vsPlus, FilterConstants.fieldTypes.NumericDecimal, '3s') + 'V',
        circuit: FilterConstants.fwCircuits.bypassCapacitor,
      });
      for (let j = 0; j < tantalumRef.length; j++) {
        if (tantalumRef[j]) {
          const tantalumRefItem = tantalumRef[j] as TantalumRef;
          const item = tantalumRefItem.ref;
          const vsupply = tantalumRefItem.voltage;
          const circuitName = tantalumRefItem.circuit;
          for (let k = 0; k < bom.length; k++) {
            if (bom[k]) {
              const bomItem = bom[k] as Bom;
              const designators = bomItem.designators,
                value = bomItem.value,
                tolerance = bomItem.tolerance;
              if (designators.includes(item)) {
                capacitorList.push({
                  item: item,
                  value: value,
                  tolerance: tolerance,
                  voltage: vsupply,
                  circuit: circuitName,
                });
                break;
              }
            }
          }
        }
      }
    }
    return capacitorList;
  }

  get needBypassCapacitors(): NeedBypassCapacitor {
    const showBypassCapacitors = this.bypassCapacitors.length > 0 ? true : false;
    return { display: showBypassCapacitors, bypassCapacitors: this.bypassCapacitors };
  }

  get needOpAmpSpares(): NeedOpAmpSpare {
    const showOpAmpSpares = this.opAmpSpares.length > 0 ? true : false;
    return { display: showOpAmpSpares, opAmpSpares: this.opAmpSpares };
  }

  get opAmpSpares(): OpAmpSpare[] {
    const sparesList = [];
    if (this.displayExtraComponents) {
      const circuitDetails = this.args.model.detailsOfCircuits;
      const vsPlus = this.args.model.componentsInput.vsPlus;
      const vsMinus = this.args.model.componentsInput.vsMinus;
      const circuitName =
        vsMinus === 0 ? FilterConstants.fwCircuits.spareAmpSingle : FilterConstants.fwCircuits.spareAmpDual;
      for (let i = 0; i < circuitDetails.length; i++) {
        if (circuitDetails[i]) {
          const circuitItem = circuitDetails[i] as DetailsOfCircuit;
          const opAmpName = circuitItem.stage.opAmp;
          const opAmpData = circuitItem.stage.opAmpSource.getOpAmp(opAmpName);
          const opAmpChannels = opAmpData.numberChannels;
          if (opAmpChannels > 1) {
            const opAmpStageLetter = circuitItem.stage.stageLetter;
            const refDesignatorOpAmp =
              getOpAmpReferenceDesignator(opAmpData.packageCode, opAmpChannels) + opAmpStageLetter;
            const numSpares = opAmpChannels - 1;
            for (let j = 0; j < numSpares; j++) {
              //let opAmpValue = "1/" + opAmpChannels.toString() + " " + opAmpName + " " + refDesignatorOpAmp;
              sparesList.push({
                circuit: circuitName,
                referenceDesignator: refDesignatorOpAmp,
                channelDesignator: '1/' + opAmpChannels.toString(),
                name: opAmpName,
                vsPlus: formatFieldValue(vsPlus, FilterConstants.fieldTypes.NumericDecimal, '3s') + 'V',
                vsMinus: formatFieldValue(vsMinus, FilterConstants.fieldTypes.NumericDecimal, '3s') + 'V',
              });
            }
          }
        }
      }
    }
    return sparesList;
  }

  get svgWidth(): number {
    const stages = this.actualStages;
    const length = stages.length;

    const referenceStage = this.needReferenceStage;
    const referenceStageWidth = referenceStage.display ? 525 : 0;

    const bypassCapacitors = this.needBypassCapacitors;
    const bypassCapacitorsWidth = bypassCapacitors.display ? bypassCapacitors.bypassCapacitors.length * 100 : 0;

    const opAmpSpares = this.needOpAmpSpares;
    let opAmpSparesWidth = opAmpSpares.display
      ? opAmpSpares.opAmpSpares.length * 100 + (opAmpSpares.opAmpSpares.length === 1 ? 70 : 5)
      : 0;
    opAmpSparesWidth += length === 1 ? 100 : 0;

    const sparesContainerWidth = referenceStageWidth + bypassCapacitorsWidth + opAmpSparesWidth;
    let schematicsContainerWidth = 0;

    if (length > 0 && stages[0] && stages[length - 1]) {
      schematicsContainerWidth =
        (stages[length - 1] as ActualStageResult).geometry.xTranslate +
        (stages[length - 1] as ActualStageResult).geometry.width -
        stages[0].geometry.xTranslate +
        35.0;
    }
    return schematicsContainerWidth >= sparesContainerWidth ? schematicsContainerWidth : sparesContainerWidth;
  }

  get referenceValues(): ReferenceValues {
    const showTolerance = this.args.model.toleranceInput ? true : false;
    const refStageVoltage = this.args.model.componentsInput.vsPlus;
    return {
      v1: { name: 'VVS1', value: refStageVoltage, tolerance: null },
      v2: { name: 'VVS2', value: refStageVoltage, tolerance: null },
      c1: { name: 'C1M', value: 0.00001, tolerance: showTolerance ? '20%' : null },
      c2: { name: 'C2M', value: 0.00001, tolerance: showTolerance ? '20%' : null },
      c3: { name: 'C3M', value: 0.0000001, tolerance: showTolerance ? '20%' : null },
      c4: { name: 'C4M', value: 0.0000001, tolerance: showTolerance ? '20%' : null },
      r1: { name: 'R1M', value: 10000, tolerance: showTolerance ? '1%' : null },
      r2: { name: 'R2M', value: 10000, tolerance: showTolerance ? '1%' : null },
      r3: { name: 'R3M', value: 4990, tolerance: showTolerance ? '1%' : null },
      r4: { name: 'R4M', value: null, tolerance: showTolerance ? '1%' : null },
      opAmp: { name: 'AD8031', value: 'U1M', url: null },
    };
  }

  getCircuitName(circuitItem: DetailsOfCircuit): string {
    let circuitName = '';
    const filterType = circuitItem.filter.name,
      implementation = circuitItem.implementation,
      hasGain =
        parseFloat(formatFieldValue(circuitItem.circuit.gain, FilterConstants.fieldTypes.Engineering, '3s')) > 1;

    switch (filterType) {
      case FilterConstants.filterType.gain: {
        circuitName = FilterConstants.fwCircuits.gain;
        break;
      }
      case FilterConstants.filterType.lowPass: {
        switch (implementation) {
          case FilterConstants.implementationType.firstOrder:
            circuitName = hasGain
              ? FilterConstants.fwCircuits.lowPassFirstOrderGain
              : FilterConstants.fwCircuits.lowPassFirstOrder;
            break;
          case FilterConstants.implementationType.sallenKey:
            circuitName = hasGain
              ? FilterConstants.fwCircuits.lowPassSallenKeyGain
              : FilterConstants.fwCircuits.lowPassSallenKey;
            break;
          case FilterConstants.implementationType.multipleFeedback:
            circuitName = hasGain
              ? FilterConstants.fwCircuits.lowPassMultipleFeedback
              : FilterConstants.fwCircuits.lowPassMultipleFeedback;
            break;
          case FilterConstants.implementationType.inductor:
            circuitName = hasGain
              ? FilterConstants.fwCircuits.lowPassInductorGain
              : FilterConstants.fwCircuits.lowPassInductor;
            break;
          default:
            break;
        }
        break;
      }
      case FilterConstants.filterType.highPass: {
        switch (implementation) {
          case FilterConstants.implementationType.firstOrder:
            circuitName = hasGain
              ? FilterConstants.fwCircuits.highPassFirstOrderGain
              : FilterConstants.fwCircuits.highPassFirstOrder;
            break;
          case FilterConstants.implementationType.sallenKey:
            circuitName = hasGain
              ? FilterConstants.fwCircuits.highPassSallenKeyGain
              : FilterConstants.fwCircuits.highPassSallenKey;
            break;
          case FilterConstants.implementationType.multipleFeedback:
            circuitName = hasGain
              ? FilterConstants.fwCircuits.highPassMultipleFeedback
              : FilterConstants.fwCircuits.highPassMultipleFeedback;
            break;
          case FilterConstants.implementationType.inductor:
            circuitName = hasGain
              ? FilterConstants.fwCircuits.highPassInductorGain
              : FilterConstants.fwCircuits.highPassInductor;
            break;
          default:
            break;
        }
        break;
      }
      case FilterConstants.filterType.bandPass: {
        switch (implementation) {
          case FilterConstants.implementationType.multipleFeedback:
          case FilterConstants.implementationType.deliyannisFriend:
            if (circuitItem.r3.value === 0 && isNearInfinity(circuitItem.r4.value))
              circuitName = FilterConstants.fwCircuits.bandPassDelyiannisFriendNoPosFB;
            else if (isNearInfinity(circuitItem.r2.value))
              circuitName = FilterConstants.fwCircuits.bandPassDelyiannisFriendPosFBnoR2;
            else circuitName = FilterConstants.fwCircuits.bandPassDelyiannisFriendPosFB;
            break;
          case FilterConstants.implementationType.inductor:
            circuitName = hasGain
              ? FilterConstants.fwCircuits.bandPassInductorGain
              : FilterConstants.fwCircuits.bandPassInductor;
            break;
          default:
            break;
        }
        break;
      }
      default:
        break;
    }
    return circuitName;
  }

  getCircuitConstants(circuitName: string): CircuitConstants {
    let result: CircuitConstants = { xTranslate: 0, yTranslate: 0, width: 0, height: 0, offset: 0 };
    switch (circuitName) {
      case FilterConstants.fwCircuits.bandPassInductorGain:
        result = { xTranslate: 0, yTranslate: 0, width: 255.168, height: 300, offset: 0 };
        break;
      case FilterConstants.fwCircuits.bandPassInductor:
        result = { xTranslate: 0, yTranslate: 0, width: 255.168, height: 300, offset: 0 };
        break;
      case FilterConstants.fwCircuits.bandPassDelyiannisFriendNoPosFB:
        result = { xTranslate: 0, yTranslate: 0, width: 296.834, height: 300, offset: 0 };
        break;
      case FilterConstants.fwCircuits.bandPassDelyiannisFriendPosFB:
        result = { xTranslate: 0, yTranslate: 0, width: 316.569, height: 300, offset: 0 };
        break;
      case FilterConstants.fwCircuits.bandPassDelyiannisFriendPosFBnoR2:
        result = { xTranslate: 0, yTranslate: 0, width: 316.569, height: 300, offset: 0 };
        break;
      case FilterConstants.fwCircuits.highPassFirstOrderGain:
        result = { xTranslate: 0, yTranslate: 0, width: 253.714, height: 300, offset: 0 };
        break;
      case FilterConstants.fwCircuits.highPassFirstOrder:
        result = { xTranslate: 0, yTranslate: 0, width: 253.714, height: 300, offset: 0 };
        break;
      case FilterConstants.fwCircuits.highPassInductorGain:
        result = { xTranslate: 0, yTranslate: 0, width: 315.168, height: 300, offset: 0 };
        break;
      case FilterConstants.fwCircuits.highPassInductor:
        result = { xTranslate: 0, yTranslate: 0, width: 315.168, height: 300, offset: 0 };
        break;
      case FilterConstants.fwCircuits.highPassMultipleFeedback:
        result = { xTranslate: 0, yTranslate: 0, width: 296.834, height: 300, offset: 0 };
        break;
      case FilterConstants.fwCircuits.highPassSallenKeyGain:
        result = { xTranslate: 0, yTranslate: 0, width: 315.168, height: 300, offset: 0 };
        break;
      case FilterConstants.fwCircuits.highPassSallenKey:
        result = { xTranslate: 0, yTranslate: 0, width: 315.168, height: 300, offset: 0 };
        break;
      case FilterConstants.fwCircuits.lowPassFirstOrderGain:
        result = { xTranslate: 0, yTranslate: 0, width: 253.714, height: 300, offset: 0 };
        break;
      case FilterConstants.fwCircuits.lowPassFirstOrder:
        result = { xTranslate: 0, yTranslate: 0, width: 253.714, height: 300, offset: 0 };
        break;
      case FilterConstants.fwCircuits.lowPassInductorGain:
        result = { xTranslate: 0, yTranslate: 0, width: 287.168, height: 300, offset: 0 };
        break;
      case FilterConstants.fwCircuits.lowPassInductor:
        result = { xTranslate: 0, yTranslate: 0, width: 287.168, height: 300, offset: 0 };
        break;
      case FilterConstants.fwCircuits.lowPassMultipleFeedback:
        result = { xTranslate: 0, yTranslate: 0, width: 296.834, height: 300, offset: 0 };
        break;
      case FilterConstants.fwCircuits.lowPassSallenKeyGain:
        result = { xTranslate: 0, yTranslate: 0, width: 315.168, height: 300, offset: 0 };
        break;
      case FilterConstants.fwCircuits.lowPassSallenKey:
        result = { xTranslate: 0, yTranslate: 0, width: 316.714, height: 300, offset: 0 };
        break;
      case FilterConstants.fwCircuits.gain:
        result = { xTranslate: 0, yTranslate: 0, width: 200, height: 300, offset: 0 };
        break;
      case FilterConstants.fwCircuits.bypassCapacitor:
      case FilterConstants.fwCircuits.tantalumCapcacitorNegative:
      case FilterConstants.fwCircuits.tantalumCapcacitorPositive:
        result = { xTranslate: 0, yTranslate: 0, width: 80, height: 80, offset: 0 };
        break;
      case FilterConstants.fwCircuits.spareAmpDual:
      case FilterConstants.fwCircuits.spareAmpSingle:
        result = { xTranslate: 0, yTranslate: 0, width: 80, height: 160, offset: 0 };
        break;
    }
    return result;
  }

  getImplementation(implementation: string): string {
    let result = '';
    switch (implementation) {
      case 'sallenKey':
        result = 'implementation.sallenKey';
        break;
      case 'deliyannisFriend':
      case 'multipleFeedback':
      case 'multipleFeedback-posFB':
      case 'multipleFeedback-noPosFB':
        result = 'implementation.multipleFeedback';
        break;
      case 'firstOrder':
        result = 'implementation.firstOrder';
        break;
      case 'inductor':
        result = 'implementation.inductor';
        break;
      default:
        break;
    }
    return result;
  }

  // useTrialData() {
  //     var principalOffset = 0;
  //     var x1 = 0,
  //         x2 = 0,
  //         showReference = true,
  //         result = [],
  //         circuits =  [
  //         // 'bandpass-multiplefeedback-noposfb',
  //         // 'highpass-multiplefeedback',
  //         // 'lowpass-multiplefeedback',
  //         // 'bandpass-multiplefeedback-posfb',
  //         // 'highpass-sallenkey',
  //         // 'lowpass-firstorder',
  //         // 'lowpass-sallenkey',
  //         // 'lowpass-multiplefeedback',
  //         // 'lowpass-sallenkey-gain',
  //         // 'highpass-sallenkey-gain',
  //         // 'bandpass-multiplefeedback-posfbnor2',
  //         // 'lowpass-sallenkey',
  //         // 'lowpass-firstorder-gain',
  //         // 'bandpass-inductor-gain',
  //         // 'lowpass-firstorder',
  //         // 'bandpass-inductor',
  //         // 'lowpass-inductor-gain',
  //         // 'highpass-firstorder-gain',
  //         // 'lowpass-inductor',
  //         // 'highpass-firstorder',
  //         'highpass-inductor-gain',
  //         'highpass-inductor',
  //         //'bpcap-circuit','tantcap-neg','tantcap-pos','spare-dual','spare-single','gain-circuit',
  //     ];
  //     for (var i=0; i<circuits.length; i++) {
  //         var nodeIn = (i==0) ? { showLine: false, showText: true } : { showLine: true, showText: false };
  //         var nodeOut = (i==circuits.length-1) ? { showLine: false, showText: true } : { showLine: true, showText: false };
  //         var nodeRef = showReference ? {showLine: false, showText: true } :  {showLine: true, showText: false };
  //         var name = circuits[i];
  //         var dimensions = this.getCircuitConstants(name.toLowerCase());

  //         if (i==0) {
  //             x1 = principalOffset;
  //             if (dimensions.offset<0) x1 = dimensions.offset;
  //             x2 = x1 + dimensions.width;
  //             if (dimensions.offset<0) x2 += dimensions.offset * -1;
  //         } else {
  //             x1 = x2;
  //             x2 = x1 + dimensions.width;
  //             if (i==circuits.length-1) {
  //                 this.set('outTxtPos', x1+dimensions.width-principalOffset-35);
  //             }
  //         }

  //         result.push({
  //             name: 'circuits/'+name.toLowerCase(),
  //             circuitId: name.toLowerCase()+'_'+i,
  //             stage: d.stage,
  //             node: {
  //                 in: nodeIn,
  //                 out: nodeOut,
  //                 ref: nodeRef
  //             },
  //             details: {
  //                 stageLetter: 'A',
  //                 stageOrder: '2',
  //                 stageFilterType: dasherize('lowPass'),
  //                 stageImplementation: dasherize('implementation.sallenKey')
  //             },
  //             geometry: {
  //                 xTranslate: x1,
  //                 yTranslate: dimensions.yTranslate,
  //                 width: dimensions.width,
  //                 height: dimensions.height
  //             },
  //             values: d
  //         });
  //     }
  //     return result;
  // }
}
